/**
 * `GET /imports/{id}/lineage` as a query — shared by `FullHistory` (the page's
 * own trail) and `LineageExpander` (a source or chain entry expanded inline),
 * so both read and write the SAME cache entry: expanding a list in the popover
 * and then navigating to it renders the cached trail at once.
 */
import { useQuery } from "@tanstack/react-query";

import type { ImportsClient } from "../../../clients/imports.client";

export function importLineageQueryKey(importId: string) {
  return ["import-lineage", importId] as const;
}

export function useImportLineage(importId: string, { client }: { client: ImportsClient }) {
  return useQuery({
    queryKey: importLineageQueryKey(importId),
    queryFn: () => client.getLineage(importId),
    // Default stale time (0), on purpose: each entry carries `total_contacts`,
    // which is `0` while a list is still being processed and fills in once it
    // finishes. A long stale time froze that `0` on screen; refetching on mount
    // shows the cached trail at once and corrects the count behind it. No
    // retry: a failed trail is a missing trail, so retrying only delays
    // rendering nothing.
    retry: false,
  });
}
